import { getSnapshot } from 'mobx-state-tree';
import React, { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import Box from 'ui-box';
import {
  Button,
  Checkbox,
  ControlGroup,
  defaultColors,
  Dialog,
  EmptyInputGroup,
  FormField,
  Grid,
  Heading,
  HTMLSelect,
  Icon,
  InputGroup,
  majorScale,
  NumberInput,
  TagInput,
  TextArea,
} from '../../npaper';
import { AddressFormControls } from '../../npaper/AddressFormControls';
import { CurrencySelect } from '../../npaper/CurrencySelect';
import { DateInput } from '../../npaper/DateInput';
import { UsersInput } from '../../npaper/UserSelect';
import {
  IAgreement,
  IAgreementSnapshotIn,
  IntervalUnit,
  useStores,
} from '../../stores';
import { CompanySelect } from '../companies/CompanySelect';
import { ContactsInput } from '../contacts/ContactSelect';

type FormValues = IAgreementSnapshotIn & {
  useDefaultAddress: boolean;
  sameAddressAsInvoice: boolean;
};

export interface IAgreementDetailsDialogProps {
  isShown: boolean;
  onClose: (agreement?: IAgreement) => void;
  agreement?: IAgreement;
  defaultValue?: Partial<FormValues>;
}

// TODO: This is mostly the same as "SaleDetailsDialog". Should we refactor the two and merge functionality?
//       Initially, I think this is not a good idea, as Sales and Agreements are semantically different things.
export const AgreementDetailsDialog: React.FC<IAgreementDetailsDialogProps> = ({
  agreement,
  defaultValue = {},
  ...props
}) => {
  const [isShown, setIsShown] = useState(props.isShown);
  const [created, setCreated] = useState<IAgreement>();
  useEffect(() => {
    setIsShown(props.isShown);
  }, [props.isShown]);

  const close = () => {
    setIsShown(false);
  };

  const { agreementStore } = useStores();

  const methods = useForm<FormValues>();
  const {
    register,
    control,
    setValue,
    errors,
    watch,
    handleSubmit,
    formState,
    reset,
  } = methods;

  useEffect(() => {
    if (!isShown) return;
    if (agreement) {
      reset(getSnapshot(agreement));
    } else {
      reset(defaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreement, isShown]);

  const onSubmit = async ({
    useDefaultAddress,
    sameAddressAsInvoice,
    ...values
  }: FormValues) => {
    if (useDefaultAddress) {
      delete values.invoice_address;
    }
    if (sameAddressAsInvoice) {
      delete values.delivery_address;
    }

    // Create or update...
    if (agreement) {
      // Update
      try {
        await agreementStore.updateItem(agreement, values);
        close();
      } catch (err) {
        // TODO: Handle error
        throw err;
      }
    } else {
      // Create
      try {
        const agreement = await agreementStore.createItem(values);
        setCreated(agreement);
        close();
      } catch (err) {
        // TODO: Handle error
        throw err;
      }
    }
  };

  return (
    <Dialog
      title={agreement ? `Rediger aftale` : 'Opret aftale'}
      isShown={isShown}
      onClose={() => props.onClose(created)}
      actions={
        <>
          <Button onClick={() => close()} disabled={formState.isSubmitting}>
            Annuller
          </Button>
          <Button
            primary
            onClick={handleSubmit(onSubmit)}
            isLoading={formState.isSubmitting}
          >
            {agreement ? 'Gem ændringer' : 'Opret aftale'}
          </Button>
        </>
      }
    >
      <FormProvider {...methods}>
        <Grid padding={majorScale(2)}>
          <Heading size={300} className="col-span-12">
            Detaljer
          </Heading>

          <FormField className="col-span-9" label="Titel" inline={true}>
            <InputGroup
              placeholder={`Titel på aftalen`}
              isInvalid={!!errors.title}
              validationMessage={errors.title && errors.title.message}
              ref={register({ required: `Du skal angive en kort titel` })}
              name="title"
            />
          </FormField>

          <FormField className="col-span-9" label="Kunde" inline={true}>
            <Controller
              control={control}
              name="customer"
              defaultValue={null}
              render={({ onChange, value }) => (
                <CompanySelect
                  onSelect={(company) => {
                    onChange(company.id);
                    if (company.currency) {
                      setValue('currency', company.currency);
                    }
                    if (company.address) {
                      setValue('invoice_address', company.address);
                    } else {
                      setValue('useDefaultAddress', false);
                    }
                  }}
                  selected={value}
                />
              )}
            />
          </FormField>

          <FormField
            className="col-span-9"
            label="Kontaktpersoner"
            inline={true}
            hint="Tilføj én eller flere kontaktpersoner til aftalen"
          >
            <Controller
              control={control}
              name="contacts"
              defaultValue={[]}
              render={({ onChange, value, onBlur }) => (
                <ContactsInput
                  onBlur={onBlur}
                  value={value}
                  filter={{ company: watch('customer') }}
                  onChange={(contacts) => {
                    onChange(contacts);
                  }}
                />
              )}
            />
          </FormField>

          <FormField className="col-span-9" label="Valuta" inline={true}>
            <Controller
              control={control}
              name="currency"
              defaultValue="DKK"
              render={({ onChange, value }) => (
                <CurrencySelect value={value} onChange={onChange} />
              )}
            />
          </FormField>

          <FormField
            className="col-span-9"
            label="Faktureringsadresse"
            inline={true}
          >
            <Controller
              control={control}
              name="useDefaultAddress"
              defaultValue={true}
              render={({ onChange, value, onBlur }) => (
                <Checkbox
                  label="Brug standard"
                  marginBottom={majorScale(1)}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
            {watch('useDefaultAddress') === false && (
              <Box>
                <AddressFormControls name="invoice_address" showCompanySelect />
              </Box>
            )}
          </FormField>

          <FormField
            className="col-span-9"
            label="Leveringsadresse"
            inline={true}
          >
            <Controller
              control={control}
              name="sameAddressAsInvoice"
              defaultValue={true}
              render={({ onChange, value, onBlur }) => (
                <Checkbox
                  label="Samme som fakturering"
                  marginBottom={majorScale(2)}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />

            {watch('sameAddressAsInvoice') === false && (
              <Box>
                <AddressFormControls
                  name="delivery_address"
                  showCompanySelect
                />
              </Box>
            )}
          </FormField>
        </Grid>
        <Grid
          borderTop={`2px solid ${defaultColors.gray[100]}`}
          padding={majorScale(2)}
        >
          <Heading size={300} className="col-span-12">
            Aftale
          </Heading>

          <FormField className="col-span-9" label="Varighed" inline={true}>
            <ControlGroup>
              <Controller
                control={control}
                name="startDate"
                render={({ onChange, value, onBlur }) => (
                  <DateInput
                    value={value}
                    onChange={onChange}
                    placeholder="Start dato"
                  />
                )}
              />
              <EmptyInputGroup>
                <Icon icon="arrow-right" />
              </EmptyInputGroup>
              <Controller
                control={control}
                name="endDate"
                render={({ onChange, value, onBlur }) => (
                  <DateInput
                    value={value}
                    onChange={onChange}
                    placeholder="Ingen slutdato"
                  />
                )}
              />
            </ControlGroup>
          </FormField>

          <FormField className="col-span-9" label="Interval" inline={true}>
            <ControlGroup>
              <EmptyInputGroup>Hver</EmptyInputGroup>
              <Controller
                control={control}
                name="interval.every"
                defaultValue={1}
                render={({ onChange, value, onBlur }) => (
                  <NumberInput
                    placeholder="Interval, eg. 1"
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
              <Controller
                control={control}
                name="interval.unit"
                defaultValue={IntervalUnit.MONTHS}
                render={({ onChange, value, onBlur }) => (
                  <HTMLSelect
                    options={Object.values(IntervalUnit).map((key) => ({
                      value: key,
                      label: key,
                    }))}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
            </ControlGroup>
          </FormField>
        </Grid>
        <Grid
          borderTop={`2px solid ${defaultColors.gray[100]}`}
          padding={majorScale(2)}
        >
          <Heading size={300} className="col-span-12">
            Administrering
          </Heading>

          <FormField className="col-span-9" label="Reference" inline={true}>
            <InputGroup
              placeholder="En reference fra salget"
              ref={register}
              defaultValue=""
              name="reference"
            />
          </FormField>

          <FormField className="col-span-9" label="Beskrivelse" inline={true}>
            <TextArea
              placeholder="En beskrivelse af salget"
              maxLength={500}
              ref={register}
              defaultValue=""
              name="description"
            />
          </FormField>

          <FormField
            className="col-span-9"
            label="Ansvarlig"
            inline={true}
            hint="Vælg én fra dit team som er ansvarlig af salget"
          >
            <Controller
              control={control}
              name="responsible"
              defaultValue={[]}
              render={({ onChange, value, onBlur }) => (
                <UsersInput
                  onBlur={onBlur}
                  value={value}
                  onChange={(users) => {
                    onChange(users.map((c) => c.id));
                  }}
                />
              )}
            />
          </FormField>

          <FormField className="col-span-9" label="Tags" inline>
            <Controller
              control={control}
              name="tags"
              defaultValue={[]}
              render={({ onChange, value, onBlur }) => (
                <TagInput value={value} onChange={onChange} onBlur={onBlur} />
              )}
            />
          </FormField>
        </Grid>
      </FormProvider>
    </Dialog>
  );
};
